import { WorkingHoursType } from '../../features/working-hours/types';
import { LangType } from '../../helpers/lang';
import { buildParams } from '../../helpers/path';
import { PaginationRequest } from '../../types/pagination';
import { CustomCacheRequest, SortRequest } from '../../types/props';
import { ImageType } from './image-api';
import { managerApiUrl } from './manager-api';
import { baseFetch } from './utils.ts/base-fetch';

export type SpecsType = {
  carbs: string;
  cooking_time_from: string;
  cooking_time_to: string;
  fats: string;
  full_desc: LangType;
  grams: string;
  kiloCalories: string;
  milliliters: string;
  proteins: string;
};

export type DishType = {
  category_id: number;
  id: string;
  images: Partial<{
    main: ImageType;
    other: ImageType[];
  }>;
  is_halal: boolean;
  is_new: boolean;
  is_on: boolean;
  name: LangType;
  full_desc: LangType;
  on_time_from: string;
  on_time_to: string;
  place_off: string[];
  prices: {
    primary: {
      old_price?: number;
      price?: number;
    };
  };
  section_id: number;
  specs: Partial<SpecsType>;
  working_hours?: WorkingHoursType;
  menu_ids: string[];
  sort_order: number;
};

const baseUrl = `${managerApiUrl}/menu`;

export type FetchDishesRequest = {
  photo?: string;
  name?: string;
  category?: string[];
  section?: string[];
  cost?: string;
  branch?: string;
  available?: string;
} & PaginationRequest &
  CustomCacheRequest &
  SortRequest;

export const fetchDishes = async (params: FetchDishesRequest): Promise<{ dishes: DishType[] }> => {
  const { customCache, ...other } = params;
  const response = await baseFetch(baseUrl + '/dish' + buildParams(other), { customCache });
  return response;
};

export const putDish = async (params: Partial<DishType>): Promise<DishType> => {
  const response = await baseFetch(baseUrl + `/dish/${params.id}`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });
  return response;
};

export const postDish = async (params: Partial<DishType>): Promise<DishType> => {
  const response = await baseFetch(baseUrl + `/dish`, {
    method: 'POST',
    body: JSON.stringify({ ...params, is_on: params.is_on || false }),
  });
  return response;
};

export interface CopyDishRequest {
  target_category_id: string;
  target_section_id: string;
  dishId: string;
}

export const copyDish = async (params: CopyDishRequest): Promise<void> => {
  const { dishId, ...other } = params;
  const response = await baseFetch(baseUrl + `/dish` + `/${dishId}/clone`, {
    method: 'POST',
    body: JSON.stringify(other),
  });
  return response;
};

const isFullDeleteParam = {
  is_full_delete: false,
};

export const deleteDish = async (id: string): Promise<DishType> => {
  const response = await baseFetch(baseUrl + `/dish/${id}` + buildParams(isFullDeleteParam), {
    method: 'DELETE',
  });
  return response;
};

export const fetchDish = async (id: string): Promise<DishType> => {
  const response = await baseFetch(baseUrl + `/dish/${id}`);
  return response;
};

export const putDishStatus = async (id: string, is_on: boolean): Promise<DishType> => {
  const response = await baseFetch(baseUrl + `/dish/${id}/change-status`, {
    method: 'PUT',
    body: JSON.stringify({ is_on }),
  });
  return response;
};

export interface PutDishOrderRequest {
  menu_id?: string;
  section_id?: number;
  category_id?: number;
  order: string[];
}

export const putDishOrder = async (req: PutDishOrderRequest) => {
  const response = await baseFetch(baseUrl + `/dish/order`, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
  return response;
};
